import { trackAuthTelemetry } from './authTelemetry';

const RECOVERY_ATTEMPTS_KEY = 'gwc_auth_recovery_attempts';
const RECOVERY_REASON_KEY = 'gwc_auth_recovery_reason';
const RECOVERY_MAX_ATTEMPTS = 2;
const RECOVERY_LAST_TRIGGER_TS_KEY = 'gwc_auth_recovery_last_trigger_ts';
const RECOVERY_COOLDOWN_MS = 10000;
let recoveryInProgress = false;

const AUTH_SESSION_KEYS = [
  'needsOnboarding',
  'postAuthRoute',
  'invitationToken',
  'pendingInvitationAcceptance',
  'selectedAccountType',
  'registrationData',
  'userProfile',
  'orgProfile',
  'subscriptionState',
  'returnUrl',
  'initial_hash_with_code',
  'initial_query_with_code'
];

const STALE_MSAL_KEY_PATTERNS = [
  /interaction\.status/i,
  /request\.state/i,
  /request\.params/i,
  /url[_-]?hash/i,
  /nonce\.id[_-]?token/i
];

function parseAttempts(value) {
  const parsed = Number.parseInt(value || '0', 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function removeAuthStorageKeys(storage) {
  AUTH_SESSION_KEYS.forEach((key) => storage.removeItem(key));

  const keys = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key) continue;
    if (key.startsWith('msal.') || key.startsWith('msal-') || key.startsWith('azuread.')) {
      keys.push(key);
    }
  }

  keys.forEach((key) => storage.removeItem(key));
}

function hasAuthResponseInUrl() {
  if (typeof window === 'undefined') return false;
  const hash = window.location.hash || '';
  const search = window.location.search || '';
  return /(code=|id_token=|error=|state=)/i.test(`${hash}${search}`);
}

function removeStaleMsalTemporaryKeys(storage) {
  const keysToRemove = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key) continue;
    if (!(key.startsWith('msal.') || key.startsWith('msal-') || key.startsWith('azuread.'))) {
      continue;
    }

    if (STALE_MSAL_KEY_PATTERNS.some((pattern) => pattern.test(key))) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => storage.removeItem(key));
  return keysToRemove.length;
}

export function runAuthStartupMaintenance() {
  if (hasAuthResponseInUrl()) {
    return;
  }

  const removedSessionKeys = removeStaleMsalTemporaryKeys(sessionStorage);
  const removedLocalKeys = removeStaleMsalTemporaryKeys(localStorage);

  if (removedSessionKeys > 0 || removedLocalKeys > 0) {
    trackAuthTelemetry('auth_startup_cleanup', {
      removedSessionKeys,
      removedLocalKeys
    });
  }
}

export function getAuthRecoveryAttempts() {
  return parseAttempts(sessionStorage.getItem(RECOVERY_ATTEMPTS_KEY));
}

export function resetAuthRecoveryAttempts() {
  sessionStorage.removeItem(RECOVERY_ATTEMPTS_KEY);
  sessionStorage.removeItem(RECOVERY_REASON_KEY);
}

export function consumeAuthRecoveryReason() {
  const reason = sessionStorage.getItem(RECOVERY_REASON_KEY);
  sessionStorage.removeItem(RECOVERY_REASON_KEY);
  return reason;
}

export async function triggerAuthRecovery({ instance, navigate, reason }) {
  if (recoveryInProgress) {
    return true;
  }

  const now = Date.now();
  const lastTrigger = parseAttempts(sessionStorage.getItem(RECOVERY_LAST_TRIGGER_TS_KEY));
  if (lastTrigger > 0 && now - lastTrigger < RECOVERY_COOLDOWN_MS) {
    return true;
  }

  const attempts = getAuthRecoveryAttempts();
  if (attempts >= RECOVERY_MAX_ATTEMPTS) {
    trackAuthTelemetry('auth_recovery_failed', {
      reason,
      attempts,
      maxAttempts: RECOVERY_MAX_ATTEMPTS
    });
    return false;
  }

  recoveryInProgress = true;
  sessionStorage.setItem(RECOVERY_LAST_TRIGGER_TS_KEY, String(now));

  trackAuthTelemetry('auth_recovery_triggered', {
    reason,
    nextAttempt: attempts + 1,
    maxAttempts: RECOVERY_MAX_ATTEMPTS
  });

  sessionStorage.setItem(RECOVERY_ATTEMPTS_KEY, String(attempts + 1));
  sessionStorage.setItem(RECOVERY_REASON_KEY, reason || 'auth_recovery');

  try {
    if (instance) {
      const accounts = instance.getAllAccounts();
      accounts.forEach((account) => {
        try {
          instance.removeAccount(account);
        } catch (error) {
          console.warn('[AuthRecovery] removeAccount failed', error);
        }
      });
      instance.setActiveAccount(null);
    }
  } catch (error) {
    console.warn('[AuthRecovery] Failed to clear MSAL account state', error);
  }

  removeAuthStorageKeys(sessionStorage);
  removeAuthStorageKeys(localStorage);

  const targetUrl = `/login?recover=1&reason=${encodeURIComponent(reason || 'auth_recovery')}`;

  if (window.location.pathname === '/login' && window.location.search.includes('recover=1')) {
    setTimeout(() => {
      recoveryInProgress = false;
    }, 1500);
    return true;
  }

  if (navigate) {
    navigate(targetUrl, { replace: true });
  } else {
    window.location.href = targetUrl;
  }

  setTimeout(() => {
    recoveryInProgress = false;
  }, 1500);

  return true;
}